import {useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {TableCancelGroup,} from "crqlar-react-common/dist/models/floorplan/floorplanEvents.dto";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {convertLocalDateToDate, formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {useResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface Props {
    cancelByTable: TableCancelGroup[];
    restaurantLabel?: string;
    onReservationsChanged?: () => void;
    onOpenReservation?: (reservation: Reservation) => void;
    // Fires only after a successful "Cancel all" — parent uses this to close the
    // host dialog. Per-row cancel and reservation-open never trigger it.
    onAllCancelled?: () => void;
}

const EAT_PERIODS = {breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner"} as const;

const EventsCancelDialogTable = ({
                                     cancelByTable,
                                     restaurantLabel,
                                     onReservationsChanged,
                                     onOpenReservation,
                                     onAllCancelled,
                                 }: Props) => {
    const {t} = useTranslation();
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();
    // Read-only users may open reservations from this list, but not cancel them.
    const readOnly = useReadOnly();
    const [cancelling, setCancelling] = useState(false);
    const [viewingId, setViewingId] = useState<string | null>(null);
    const [cancelledIds, setCancelledIds] = useState<string[]>([]);
    const [pendingIds, setPendingIds] = useState<string[]>([]);
    const [pendingIsBulk, setPendingIsBulk] = useState(false);

    const tableResources = useResources(cancelByTable.map(g => g.tableId));

    // Hide orphans whose reservation has already been cancelled in this session.
    // The backend list refreshes via onReservationsChanged but local state hides them
    // immediately so the UI stays responsive.
    const visibleRows = cancelByTable.flatMap(group =>
        group.orphans
            .filter(o => !o.reservationId || !cancelledIds.includes(o.reservationId))
            .map(orphan => ({group, orphan}))
    );
    const allReservationIds = visibleRows
        .map(r => r.orphan.reservationId)
        .filter((id): id is string => !!id);

    // Cancel a batch of reservations. We re-fetch to get current snapshots, mark them
    // CANCELLED, then show a snackbar with an Undo action that restores the originals.
    const cancelReservations = async (ids: string[]) => {
        if (!ids.length) return false;

        setCancelling(true);
        try {
            const page = await ReservationService.getAllReservations({
                id: ids,
                limit: {offset: 0, limit: ids.length},
            });
            const found = (page.content ?? []).filter(r => r.id && ids.includes(r.id));
            if (!found.length) throw new Error("No reservations found to cancel");

            await ReservationService.updateMulti(
                found.map(r => ({...r, status: ReservationStatus.CANCELLED}))
            );

            const newlyCancelled = found.map(r => r.id!).filter(Boolean);
            setCancelledIds(current => current.concat(newlyCancelled));
            onReservationsChanged?.();

            enqueueSnackbar(t("Reservations cancelled"), {
                variant: "success",
                autoHideDuration: 5000,
                action: (snackbarId) => (
                    <>
                        <Button
                            color="inherit"
                            size="small"
                            onClick={async () => {
                                closeSnackbar(snackbarId);
                                try {
                                    await ReservationService.updateMulti(found);
                                    setCancelledIds(current =>
                                        current.filter(id => newlyCancelled.indexOf(id) === -1)
                                    );
                                    onReservationsChanged?.();
                                } catch (error) {
                                    console.error(error);
                                    enqueueSnackbar(t("Failed to undo reservation cancellation"), {variant: "error", autoHideDuration: 5000});
                                }
                            }}
                        >
                            {t("Undo")}
                        </Button>
                        <IconButton size="small" color="inherit" onClick={() => closeSnackbar(snackbarId)}>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </>
                ),
            });
            return true;
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t("Failed to cancel reservations"), {
                variant: "error",
                autoHideDuration: 5000,
            });
            return false;
        } finally {
            setCancelling(false);
        }
    };

    const openReservation = async (reservationId: string) => {
        if (!onOpenReservation) return;

        setViewingId(reservationId);
        try {
            const reservation = await ReservationService.getReservationById(reservationId);
            if (!reservation) throw new Error("Reservation not found");
            onOpenReservation(reservation);
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t("Failed to open reservation"), {
                variant: "error",
                autoHideDuration: 5000,
            });
        } finally {
            setViewingId(null);
        }
    };

    const handleConfirm = async () => {
        const wasBulk = pendingIsBulk;
        // Treat cancelling the last visible row the same as a bulk cancel —
        // there's nothing left to do, so close the host dialog.
        const wasLastOne = !wasBulk && visibleRows.length === 1;
        const ok = await cancelReservations(pendingIds);
        if (ok) {
            setPendingIds([]);
            setPendingIsBulk(false);
            if (wasBulk || wasLastOne) onAllCancelled?.();
        }
    };

    if (!visibleRows.length) {
        return (
            <Typography color="text.secondary" sx={{px: 3, pb: 2}}>
                {t("No outstanding cancellations")}
            </Typography>
        );
    }

    return (
        <>
            <Box sx={{px: 3, pb: 2}}>
                <Box sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 0.75,
                    p: 1.5,
                    bgcolor: "background.paper",
                }}>
                    {/* Toolbar: row count + bulk "Cancel all" trigger. */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        pb: 1,
                        flexWrap: "wrap",
                    }}>
                        <Typography color="text.secondary">
                            {t("{{count}} reservations to cancel", {
                                count: visibleRows.length,
                            })}
                        </Typography>
                        <Button
                            color="error"
                            variant="contained"
                            size="small"
                            disabled={!allReservationIds.length || cancelling || readOnly}
                            onClick={() => {
                                setPendingIds(allReservationIds);
                                setPendingIsBulk(true);
                            }}
                        >
                            {t("Cancel all")}
                        </Button>
                    </Box>

                    {/* Desktop column headers. Hidden on mobile because rows stack vertically.
                        Fixed widths for the data columns + 1fr for actions so buttons sit on
                        the far right and the data stays compactly grouped on the left. */}
                    <Box sx={{
                        display: {xs: "none", sm: "grid"},
                        gridTemplateColumns: "110px 110px 160px 1fr",
                        gap: 2,
                        pb: 0.75,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                textTransform: "uppercase",
                                color: "text.secondary",
                                letterSpacing: 0.5,
                            }}
                        >
                            {t("Date")}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                textTransform: "uppercase",
                                color: "text.secondary",
                                letterSpacing: 0.5,
                            }}
                        >
                            {t("Period")}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                textTransform: "uppercase",
                                color: "text.secondary",
                                letterSpacing: 0.5,
                            }}
                        >
                            {t("Table")}
                        </Typography>
                        <Box/>
                    </Box>

                    {visibleRows.map(({group, orphan}, index) => {
                        const id = orphan.reservationId;

                        // Cell 1 — Date (top) / time range (bottom).
                        const startDate = convertLocalDateToDate(orphan.startDay ?? undefined);
                        const endDate = convertLocalDateToDate(orphan.endDay ?? undefined);
                        const startDay = startDate ? formatFullDMY(startDate) : "";
                        const endDay = endDate ? formatFullDMY(endDate) : "";
                        const dateLabel = startDay && endDay && startDay !== endDay
                            ? `${startDay} - ${endDay}`
                            : startDay || endDay;
                        const startTime = orphan.startTime?.slice(0, 5) ?? "";
                        const endTime = orphan.endTime?.slice(0, 5) ?? "";
                        const timeLabel = startTime && endTime
                            ? `${startTime}-${endTime}`
                            : startTime || endTime;

                        // Cell 2 — Eat period.
                        const periodKey = orphan.eatPeriod && EAT_PERIODS[orphan.eatPeriod];
                        const periodLabel = periodKey ? t(periodKey) : "";

                        // Cell 3 — Table name (top) / restaurant (bottom). No fallback to ID:
                        // missing names render as empty so the data issue stays visible.
                        const tableLabel = tableResources.find(r => r.id === group.tableId)?.name;

                        return (
                            <Box
                                key={`${group.tableId}-${id ?? orphan.profileId}-${orphan.startDay}-${orphan.startTime}`}
                                sx={{
                                    // Desktop: fixed-width data columns (Date | Period | Table) +
                                    // a 1fr actions column so buttons stay right-aligned.
                                    // Mobile: single column, each cell stacks; actions at the bottom.
                                    display: "grid",
                                    gridTemplateColumns: {xs: "1fr", sm: "110px 110px 160px 1fr"},
                                    gap: {xs: 0.5, sm: 2},
                                    alignItems: "center",
                                    py: 1.25,
                                    borderTop: index === 0 ? 0 : "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Box>
                                    <Typography sx={{fontWeight: 700, lineHeight: 1.3}}>
                                        {dateLabel || "-"}
                                    </Typography>
                                    {timeLabel && (
                                        <Typography color="text.secondary" sx={{lineHeight: 1.3}}>
                                            {timeLabel}
                                        </Typography>
                                    )}
                                </Box>

                                <Typography color="text.secondary" sx={{lineHeight: 1.3, alignSelf: "start"}}>
                                    {periodLabel || "-"}
                                </Typography>

                                <Box sx={{minWidth: 0}}>
                                    {tableLabel && (
                                        <Typography sx={{
                                            lineHeight: 1.3,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}>
                                            {tableLabel}
                                        </Typography>
                                    )}
                                    {restaurantLabel && (
                                        <Typography color="text.secondary" sx={{
                                            lineHeight: 1.3,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}>
                                            {restaurantLabel}
                                        </Typography>
                                    )}
                                </Box>

                                <Stack
                                    direction="row"
                                    gap={1}
                                    justifyContent={{xs: "flex-start", sm: "flex-end"}}
                                    sx={{mt: {xs: 0.5, sm: 0}}}
                                >
                                    {onOpenReservation && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={!id || viewingId === id}
                                            onClick={() => id && openReservation(id)}
                                        >
                                            {t("Reservation")}
                                        </Button>
                                    )}
                                    <Button
                                        color="error"
                                        variant="outlined"
                                        size="small"
                                        disabled={!id || cancelling || readOnly}
                                        onClick={() => {
                                            if (!id) return;
                                            setPendingIds([id]);
                                            setPendingIsBulk(false);
                                        }}
                                    >
                                        {t("Cancel")}
                                    </Button>
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Dialog
                open={pendingIds.length > 0}
                onClose={() => {
                    if (cancelling) return;
                    setPendingIds([]);
                    setPendingIsBulk(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{fontWeight: 700}}>
                    {t("Cancel reservations?")}
                </DialogTitle>
                <DialogContent>
                    {pendingIsBulk ? (
                        <Typography color="text.secondary">
                            {t("This will cancel all outstanding restaurant reservations.")}
                        </Typography>
                    ) : (() => {
                        const orphan = cancelByTable
                            .flatMap(group => group.orphans.map(o => ({orphan: o, tableId: group.tableId})))
                            .find(({orphan}) => orphan.reservationId && pendingIds.includes(orphan.reservationId));

                        if (!orphan) return null;

                        const startDate = convertLocalDateToDate(orphan.orphan.startDay ?? undefined);
                        const dateLabel = startDate ? formatFullDMY(startDate) : "";
                        const startTime = orphan.orphan.startTime?.slice(0, 5) ?? "";
                        const endTime = orphan.orphan.endTime?.slice(0, 5) ?? "";
                        const timeLabel = startTime && endTime
                            ? `${startTime}-${endTime}`
                            : startTime || endTime;
                        const dateTimeLabel = [dateLabel, timeLabel].filter(Boolean).join(" ");
                        const periodKey = orphan.orphan.eatPeriod && EAT_PERIODS[orphan.orphan.eatPeriod];
                        const periodLabel = periodKey ? t(periodKey) : "";
                        const tableLabel = tableResources.find(r => r.id === orphan.tableId)?.name ?? "-";
                        const parts = [dateTimeLabel, periodLabel, tableLabel].filter(Boolean).join(" · ");

                        return (
                            <Typography color="text.secondary">
                                {t("Cancel reservation:")}
                                {" "}
                                <Box component="span" sx={{fontWeight: 700, color: "text.primary"}}>
                                    {parts}
                                </Box>
                                {"?"}
                            </Typography>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setPendingIds([]);
                            setPendingIsBulk(false);
                        }}
                        disabled={cancelling}
                    >
                        {t("Back")}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirm}
                        disabled={cancelling}
                        startIcon={cancelling ? <CircularProgress color="inherit" size={16}/> : undefined}
                    >
                        {t("Cancel")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EventsCancelDialogTable;
